<template>
  <div ref="playerContainer" class="player-content player-container"></div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import { nextTick } from "vue";

declare global {
  interface Window {
    EasyPlayerPro?: new (container: HTMLElement, config: Record<string, any>) => any;
  }
}

const EASY_PLAYER_SCRIPT_ID = "easy-player-pro-script";
const EASY_PLAYER_BASE = `${process.env.BASE_URL || "/"}easyplayer/`;
const EASY_PLAYER_SCRIPT_PRELOAD_ID = "easy-player-pro-script-preload";
const EASY_PLAYER_WASM_PRELOAD_ID = "easy-player-pro-wasm-preload";
const MAX_PLAY_RETRY = 2;
const PLAY_RETRY_DELAY = 1200;
const PLAYER_CACHE_CLEANUP_INTERVAL = 60 * 1000;
const PLAYER_RECYCLE_INTERVAL = 30 * 60 * 1000;
const PLAYER_RECYCLE_RESTART_DELAY = 300;

let easyPlayerLoader: Promise<void> | null = null;
let easyPlayerWasmWarmup: Promise<void> | null = null;

function preloadAsset(id: string, href: string, as: string, type?: string) {
  if (document.getElementById(id)) {
    return;
  }

  const link = document.createElement("link");
  link.id = id;
  link.rel = "preload";
  link.href = href;
  link.as = as;

  if (type) {
    link.type = type;
  }

  if (as === "fetch") {
    link.crossOrigin = "anonymous";
  }

  document.head.appendChild(link);
}

function preloadEasyPlayerAssets() {
  preloadAsset(EASY_PLAYER_SCRIPT_PRELOAD_ID, `${EASY_PLAYER_BASE}EasyPlayer-pro.js`, "script");
  preloadAsset(EASY_PLAYER_WASM_PRELOAD_ID, `${EASY_PLAYER_BASE}EasyPlayer-pro.wasm`, "fetch", "application/wasm");
}

function warmEasyPlayerWasm(): Promise<void> {
  if (easyPlayerWasmWarmup) {
    return easyPlayerWasmWarmup;
  }

  easyPlayerWasmWarmup = Promise.resolve();

  return easyPlayerWasmWarmup;
}

// 缓存脚本加载 Promise，避免切换视频时重复插入 EasyPlayer 脚本。
function loadEasyPlayer(): Promise<void> {
  if (window.EasyPlayerPro) {
    return Promise.resolve();
  }

  if (easyPlayerLoader) {
    return easyPlayerLoader;
  }

  easyPlayerLoader = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(EASY_PLAYER_SCRIPT_ID) as HTMLScriptElement | null;

    if (existingScript) {
      if (existingScript.dataset.loaded === "true") {
        resolve();
        return;
      }
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("EasyPlayer.js 加载失败")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = EASY_PLAYER_SCRIPT_ID;
    script.src = `${EASY_PLAYER_BASE}EasyPlayer-pro.js`;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () => reject(new Error("EasyPlayer.js 加载失败"));
    document.body.appendChild(script);
  });

  return easyPlayerLoader;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function waitForFrame(): Promise<void> {
  return new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
}

function getErrorMessage(error: any): string {
  if (!error) {
    return "";
  }

  if (typeof error === "string") {
    return error;
  }

  return error.message || error.msg || String(error);
}

function isUserGesturePlayError(error: any): boolean {
  const message = getErrorMessage(error).toLowerCase();

  return message.includes("user gesture") || message.includes("notallowederror");
}

function isMobileBrowser(): boolean {
  return /android|iphone|ipad|ipod|mobile|micromessenger/i.test(window.navigator.userAgent);
}

function releaseMediaElements(root: HTMLElement) {
  const mediaElements = root.querySelectorAll("video, audio");

  mediaElements.forEach((mediaElement) => {
    const media = mediaElement as HTMLMediaElement & { srcObject?: any };

    try {
      media.pause();
      const mediaSrc = media.currentSrc || media.src;

      if (mediaSrc && mediaSrc.startsWith("blob:")) {
        URL.revokeObjectURL(mediaSrc);
      }

      media.removeAttribute("src");

      if ("srcObject" in media) {
        media.srcObject = null;
      }

      media.load();
    } catch (error) {
      console.warn("Release media element failed", error);
    }
  });
}

@Options({
  props: {},
  expose: ["playVideoStreamEvent"],
  mounted() {
    this.playerContainer = this.$refs.playerContainer as HTMLElement;
    this.installUserGestureErrorFilter();
    this.installPageVisibilityHandler();
    preloadEasyPlayerAssets();
    warmEasyPlayerWasm();
    loadEasyPlayer().catch((error) => {
      console.error("EasyPlayer preload failed", error);
    });
  },
  beforeUnmount() {
    this.playTaskId += 1;
    this.videoSrc = "";
    this.uninstallUserGestureErrorFilter();
    this.uninstallPageVisibilityHandler();
    this.destroyPlayer();
  },
})
export default class PlayerFull extends Vue {
  videoSrc: string = "";
  playerContainer: HTMLElement | null = null;
  playerHost: HTMLElement | null = null;
  easyPlayer: any = null;
  // 标记当前播放请求，用于丢弃已过期的异步播放任务。
  playTaskId: number = 0;
  userGestureErrorHandler: ((event: ErrorEvent) => void) | null = null;
  userGestureRejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;
  pageVisibilityHandler: (() => void) | null = null;
  playerErrorHandler: ((error: any) => void) | null = null;
  cacheCleanupTimer: number | null = null;
  playerStartedAt: number = 0;
  isRecyclingPlayer: boolean = false;

  installUserGestureErrorFilter() {
    if (this.userGestureErrorHandler || this.userGestureRejectionHandler) {
      return;
    }

    this.userGestureErrorHandler = (event: ErrorEvent) => {
      if (!isUserGesturePlayError(event.error || event.message)) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
    };

    this.userGestureRejectionHandler = (event: PromiseRejectionEvent) => {
      if (!isUserGesturePlayError(event.reason)) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
    };

    window.addEventListener("error", this.userGestureErrorHandler, true);
    window.addEventListener("unhandledrejection", this.userGestureRejectionHandler, true);
  }

  uninstallUserGestureErrorFilter() {
    if (this.userGestureErrorHandler) {
      window.removeEventListener("error", this.userGestureErrorHandler, true);
      this.userGestureErrorHandler = null;
    }

    if (this.userGestureRejectionHandler) {
      window.removeEventListener("unhandledrejection", this.userGestureRejectionHandler, true);
      this.userGestureRejectionHandler = null;
    }
  }

  installPageVisibilityHandler() {
    if (this.pageVisibilityHandler) {
      return;
    }

    this.pageVisibilityHandler = () => {
      if (!document.hidden) {
        return;
      }

      this.trimPlayerCache();
    };

    document.addEventListener("visibilitychange", this.pageVisibilityHandler);
  }

  uninstallPageVisibilityHandler() {
    if (!this.pageVisibilityHandler) {
      return;
    }

    document.removeEventListener("visibilitychange", this.pageVisibilityHandler);
    this.pageVisibilityHandler = null;
  }

  trimPlayerCache(player: any = this.easyPlayer) {
    if (!player) {
      return;
    }

    try {
      if (typeof player.clearCache === "function") {
        player.clearCache();
      }
    } catch (error) {
      console.warn("EasyPlayer clear cache failed", error);
    }
  }

  stopPlayerMaintenance() {
    if (this.cacheCleanupTimer) {
      window.clearInterval(this.cacheCleanupTimer);
      this.cacheCleanupTimer = null;
    }
  }

  startPlayerMaintenance(taskId: number) {
    this.stopPlayerMaintenance();
    this.playerStartedAt = Date.now();
    this.cacheCleanupTimer = window.setInterval(() => {
      if (taskId !== this.playTaskId || !this.easyPlayer || !this.videoSrc) {
        this.stopPlayerMaintenance();
        return;
      }

      this.trimPlayerCache();

      if (Date.now() - this.playerStartedAt >= PLAYER_RECYCLE_INTERVAL) {
        this.recycleCurrentPlayer(taskId);
      }
    }, PLAYER_CACHE_CLEANUP_INTERVAL);
  }

  async recycleCurrentPlayer(taskId: number) {
    if (this.isRecyclingPlayer || taskId !== this.playTaskId || !this.videoSrc) {
      return;
    }

    this.isRecyclingPlayer = true;
    const currentUrl = this.videoSrc;
    this.playTaskId += 1;
    const nextTaskId = this.playTaskId;

    try {
      this.destroyPlayer();
      await delay(PLAYER_RECYCLE_RESTART_DELAY);

      if (this.videoSrc === currentUrl && nextTaskId === this.playTaskId) {
        await this.playEasyPlayerVideo(nextTaskId);
      }
    } finally {
      this.isRecyclingPlayer = false;
    }
  }

  bindPlayerErrorHandler(player: any) {
    this.playerErrorHandler = (error: any) => {
      if (isUserGesturePlayError(error)) {
        return;
      }

      console.error("EasyPlayer error", error);
    };

    player.on("error", this.playerErrorHandler);
  }

  unbindPlayerErrorHandler(player: any = this.easyPlayer) {
    if (!player || !this.playerErrorHandler) {
      this.playerErrorHandler = null;
      return;
    }

    try {
      if (typeof player.off === "function") {
        player.off("error", this.playerErrorHandler);
      } else if (typeof player.removeListener === "function") {
        player.removeListener("error", this.playerErrorHandler);
      }
    } catch (error) {
      console.warn("EasyPlayer remove error handler failed", error);
    } finally {
      this.playerErrorHandler = null;
    }
  }

  destroyPlayer() {
    this.stopPlayerMaintenance();

    if (!this.easyPlayer) {
      this.clearPlayerHost();
      return;
    }

    const player = this.easyPlayer;

    try {
      this.unbindPlayerErrorHandler(player);
      this.trimPlayerCache(player);

      if (typeof player.pause === "function") {
        const pauseResult = player.pause();
        if (pauseResult && typeof pauseResult.catch === "function") {
          pauseResult.catch((error: any) => {
            console.warn("EasyPlayer pause before destroy failed", error);
          });
        }
      }

      if (typeof player.destroy === "function") {
        player.destroy();
      }
    } catch (error) {
      console.warn("EasyPlayer destroy failed", error);
    } finally {
      this.easyPlayer = null;
      this.clearPlayerHost();
    }
  }
  // https://play.bolebo.cn?
  clearPlayerHost() {
    if (!this.playerContainer) {
      return;
    }

    releaseMediaElements(this.playerContainer);
    // EasyPlayer 会在容器上写入内部标记；切流时必须换一个干净的宿主节点。
    this.playerContainer.innerHTML = "";
    this.playerHost = null;
  }

  createPlayerHost() {
    if (!this.playerContainer) {
      return null;
    }

    this.clearPlayerHost();
    const host = document.createElement("div");
    host.className = "easy-player-host";
    // 动态创建的节点不带 Vue scoped 属性，关键尺寸需要直接写入行内样式。
    host.style.display = "block";
    host.style.width = "100%";
    host.style.height = "100%";
    host.style.minHeight = "180px";
    this.playerContainer.appendChild(host);
    this.playerHost = host;
    return host;
  }

  async waitForPlayerLayout(taskId: number) {
    for (let index = 0; index < 10; index += 1) {
      await waitForFrame();

      if (taskId !== this.playTaskId || !this.playerContainer) {
        return false;
      }

      const rect = this.playerContainer.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        return true;
      }
    }

    return false;
  }

  createEasyPlayer(playerHost: HTMLElement) {
    const EasyPlayerPro = window.EasyPlayerPro;
    if (!EasyPlayerPro) {
      throw new Error("EasyPlayerPro is not loaded");
    }

    const useMobileWasm = isMobileBrowser();

    return new EasyPlayerPro(playerHost, {
      isLive: true,
      videoBuffer: 300,
      videoBufferDelay: 700,
      stretch: false,
      // 监控场景默认静音，避免刷新后被 Chrome 的音频自动播放策略拦截。
      hasAudio: false,
      muted: true,
      isMute: true,
      useMSE: true,
      useWCS: true,
      useWasm: false,
      autoWasm: true,
      decoderErrorAutoWasm: true,
      hardDecodingNotSupportAutoWasm: true,
      demuxUseWorker: false,
      loadingTimeout: useMobileWasm ? 45 : 30,
      heartTimeout: useMobileWasm ? 60 : 45,
      loadingTimeoutReplay: false,
      loadingTimeoutReplayTimes: 0,
      heartTimeoutReplay: false,
      heartTimeoutReplayTimes: 0,
      networkDelayTimeoutReplay: false,
      playFailedAndReplay: false,
      streamErrorReplay: false,
      // EasyPlayer 的 worker/wasm 静态资源统一放在 public/easyplayer 下。
      decoderPath: EASY_PLAYER_BASE,
      isBand: true,
      btns: {
        play: true,
        audio: false,
        record: false,
        zoom: true,
        ptz: false,
        quality: false,
        stretch: true,
        screenshot: true,
        fullscreen: true,
      },
    });
  }

  async playEasyPlayerVideo(taskId: number, retryCount: number = 0) {
    await nextTick();

    if (taskId !== this.playTaskId || !this.videoSrc) {
      return;
    }

    this.playerContainer = this.playerContainer || (this.$refs.playerContainer as HTMLElement);

    if (!this.playerContainer) {
      return;
    }

    this.destroyPlayer();
    await loadEasyPlayer();
    await warmEasyPlayerWasm();
    const hasLayout = await this.waitForPlayerLayout(taskId);

    if (taskId !== this.playTaskId || !this.videoSrc || !window.EasyPlayerPro || !this.playerContainer || !hasLayout) {
      return;
    }

    const playerHost = this.createPlayerHost();

    if (!playerHost) {
      return;
    }

    this.easyPlayer = this.createEasyPlayer(playerHost);
    this.bindPlayerErrorHandler(this.easyPlayer);

    try {
      await this.easyPlayer.play(this.videoSrc);
      if (taskId === this.playTaskId) {
        this.startPlayerMaintenance(taskId);
      }
    } catch (error) {
      if (isUserGesturePlayError(error)) {
        return;
      }

      console.error("EasyPlayer play failed", error);

      if (taskId === this.playTaskId && retryCount < MAX_PLAY_RETRY) {
        await delay(PLAY_RETRY_DELAY);
        await this.playEasyPlayerVideo(taskId, retryCount + 1);
      }
    }
  }

  playVideoStreamEvent = (url: string) => {
    this.videoSrc = url;
    this.playTaskId += 1;

    if (!url) {
      this.destroyPlayer();
      return;
    }
    this.playEasyPlayerVideo(this.playTaskId);
  };
}
</script>

<style lang="css" scoped>
.player-content {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 180px;
  background: #000;
}

.player-container {
  position: relative;
  overflow: hidden;
}

.easy-player-host {
  width: 100%;
  height: 100%;
}
</style>
